import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HourglassBottomOutlinedIcon from '@mui/icons-material/HourglassBottomOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import { LoadingButton } from '@mui/lab';
import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { claimDelivery, getDeliveryByToken } from '../../data/api/moveDeliveries/MoveDeliveryRoutes';
import useIndex from '../../hooks/useIndex';

/**
 * Página pública accesible vía el QR generado por el distribuidor:
 * `/claim-moves/:token`.
 *
 * Estados:
 *   - loading inicial (consulta info pública del token)
 *   - QR no encontrado / expirado / ya reclamado → mensaje y CTA a inicio
 *   - sin sesión → guarda token en localStorage y ofrece login/registro
 *   - con sesión + Jugador → reclamo automático al montar
 *   - con sesión + no Jugador → mensaje claro
 */
const PENDING_CLAIM_KEY = 'pending_claim_token';

const ClaimMoves = () => {
    const { token: claimToken } = useParams();
    const navigate = useNavigate();
    const { auth, token } = useIndex();

    const [info, setInfo] = useState(null);
    const [loadingInfo, setLoadingInfo] = useState(true);
    const [infoError, setInfoError] = useState(null);

    const [claiming, setClaiming] = useState(false);
    const [claimResult, setClaimResult] = useState(null);
    const [claimError, setClaimError] = useState(null);
    const claimTriggered = useRef(false);

    // Identificación del rol Jugador. Mantengo el id alineado con el
    // resto del front (config/roles.js — ROLE_IDS.JUGADOR = 2).
    const isLogged = !!token && !!auth?.id;
    const isPlayer = isLogged && Number(auth?.role_id) === 2;

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await getDeliveryByToken(claimToken);
                if (cancelled) return;
                if (data?.success) {
                    setInfo(data.data);
                } else {
                    setInfoError(data?.message ?? 'No se pudo cargar el QR.');
                }
            } catch (e) {
                if (!cancelled) setInfoError(
                    e?.response?.data?.error
                    ?? e?.response?.data?.message
                    ?? 'QR no encontrado o expirado.'
                );
            } finally {
                if (!cancelled) setLoadingInfo(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [claimToken]);

    // Auto-claim cuando llega al claim ya autenticado como jugador con
    // un QR válido. claimTriggered evita doble disparo si React monta
    // dos veces en strict mode.
    useEffect(() => {
        if (!info || info.status !== 'pending') return;
        if (!isPlayer) return;
        if (claimTriggered.current) return;
        claimTriggered.current = true;
        runClaim();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [info, isPlayer]);

    const runClaim = async () => {
        setClaiming(true);
        setClaimError(null);
        try {
            const { data } = await claimDelivery(token, claimToken);
            if (data?.success) {
                setClaimResult(data.data);
                // Limpia el pending por si el flujo vino vía login.
                try { localStorage.removeItem(PENDING_CLAIM_KEY); } catch { /* noop */ }
            }
        } catch (e) {
            setClaimError(
                e?.response?.data?.error
                ?? e?.response?.data?.message
                ?? 'No se pudo reclamar la entrega.'
            );
        } finally {
            setClaiming(false);
        }
    };

    const goToLogin = () => {
        try { localStorage.setItem(PENDING_CLAIM_KEY, claimToken); } catch { /* noop */ }
        navigate('/');
    };

    return (
        <Wrapper>
            <Card>
                <Header>
                    <Box className="icon-box">
                        <RedeemOutlinedIcon />
                    </Box>
                    <Box>
                        <Typography className="title">Recibir jugadas</Typography>
                        <Typography className="subtitle">
                            Reclama las jugadas que un distribuidor te entregó.
                        </Typography>
                    </Box>
                </Header>

                {loadingInfo ? (
                    <Centered>
                        <CircularProgress size={28} />
                        <Typography className="muted">Cargando información del QR…</Typography>
                    </Centered>
                ) : infoError ? (
                    <Status type="error">
                        <ErrorOutlineOutlinedIcon />
                        <Typography>{infoError}</Typography>
                    </Status>
                ) : (
                    <>
                        <InfoBlock>
                            <Box className="row">
                                <span>Distribuidor</span>
                                <strong>
                                    {info?.distributor?.name} {info?.distributor?.lastname ?? ''}
                                </strong>
                            </Box>
                            <Box className="row">
                                <span>Jugadas</span>
                                <strong>{info?.moves}</strong>
                            </Box>
                            {info?.expires_at && info?.status === 'pending' && (
                                <Box className="row">
                                    <span>Expira</span>
                                    <strong>{new Date(info.expires_at).toLocaleString()}</strong>
                                </Box>
                            )}
                        </InfoBlock>

                        {info?.status === 'claimed' && (
                            <Status type="info">
                                <CheckCircleOutlineIcon />
                                <Typography>Esta entrega ya fue reclamada.</Typography>
                            </Status>
                        )}

                        {info?.status === 'expired' && (
                            <Status type="error">
                                <HourglassBottomOutlinedIcon />
                                <Typography>El QR ya expiró. Pídele al distribuidor que genere uno nuevo.</Typography>
                            </Status>
                        )}

                        {info?.status === 'pending' && (
                            <>
                                {!isLogged && (
                                    <>
                                        <Status type="info">
                                            <Typography>
                                                Inicia sesión o crea una cuenta de Jugador para recibir tus jugadas.
                                            </Typography>
                                        </Status>
                                        <LoadingButton
                                            fullWidth
                                            variant="contained"
                                            startIcon={<LoginOutlinedIcon />}
                                            onClick={goToLogin}
                                            sx={primaryButton}
                                        >
                                            <span>Iniciar sesión / Registrarme</span>
                                        </LoadingButton>
                                    </>
                                )}

                                {isLogged && !isPlayer && (
                                    <Status type="error">
                                        <ErrorOutlineOutlinedIcon />
                                        <Typography>
                                            Solo un Jugador puede reclamar entregas. La sesión actual no corresponde a un jugador.
                                        </Typography>
                                    </Status>
                                )}

                                {isLogged && isPlayer && (
                                    <>
                                        {claimResult ? (
                                            <Status type="success">
                                                <CheckCircleOutlineIcon />
                                                <Typography>
                                                    ¡Listo! Recibiste {claimResult?.moves} jugadas.
                                                </Typography>
                                            </Status>
                                        ) : claimError ? (
                                            <>
                                                <Status type="error">
                                                    <ErrorOutlineOutlinedIcon />
                                                    <Typography>{claimError}</Typography>
                                                </Status>
                                                <LoadingButton
                                                    fullWidth
                                                    variant="contained"
                                                    loading={claiming}
                                                    onClick={runClaim}
                                                    sx={primaryButton}
                                                >
                                                    <span>Reintentar</span>
                                                </LoadingButton>
                                            </>
                                        ) : (
                                            <Centered>
                                                <CircularProgress size={28} />
                                                <Typography className="muted">Reclamando jugadas…</Typography>
                                            </Centered>
                                        )}

                                        {claimResult && (
                                            <LoadingButton
                                                fullWidth
                                                variant="contained"
                                                onClick={() => navigate('/ganacol/dashboard/game')}
                                                sx={primaryButton}
                                            >
                                                <span>Ir a jugar</span>
                                            </LoadingButton>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </Card>
        </Wrapper>
    );
};

export default ClaimMoves;

const primaryButton = {
    mt: 1.5,
    borderRadius: '12px',
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
    '&:hover': {
        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
    },
};

const Wrapper = styled.div`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: linear-gradient(180deg, #f6f8ff 0%, #eef2fb 100%);
`;

const Card = styled.div`
    width: 100%;
    max-width: 460px;
    border-radius: 18px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    box-shadow: 0 18px 38px rgba(15, 34, 77, 0.12);
    padding: 1.4rem;
    overflow: hidden;
    position: relative;

    &::before {
        content: "";
        position: absolute;
        top: 0; left: 0; right: 0;
        height: 5px;
        background: linear-gradient(90deg, #10285f 0%, #d6aa3a 100%);
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 0.7rem;
    margin-bottom: 1rem;

    .icon-box {
        width: 42px;
        height: 42px;
        border-radius: 12px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .title {
        color: #10285f;
        font-weight: 900;
        font-size: 1.15rem;
        line-height: 1.2;
    }

    .subtitle {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.82rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }
`;

const InfoBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-bottom: 0.8rem;

    .row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.55rem 0.85rem;
        border-radius: 12px;
        background: rgba(16, 40, 95, 0.045);
        border: 1px solid rgba(15, 34, 77, 0.08);

        span {
            color: rgba(15, 34, 77, 0.62);
            font-size: 0.74rem;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.4px;
        }

        strong {
            color: #10285f;
            font-size: 0.92rem;
            font-weight: 900;
            text-align: right;
            word-break: break-word;
        }
    }
`;

const Centered = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
    padding: 1rem 0;

    .muted {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.85rem;
        font-weight: 700;
    }
`;

const Status = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.7rem 0.85rem;
    border-radius: 12px;
    margin-bottom: 0.6rem;

    ${({ type }) => type === 'success' && `
        color: #0f5c36;
        background: rgba(46, 125, 50, 0.12);
        border: 1px solid rgba(46, 125, 50, 0.25);
    `}
    ${({ type }) => type === 'error' && `
        color: #9b1c1c;
        background: rgba(211, 47, 47, 0.10);
        border: 1px solid rgba(211, 47, 47, 0.25);
    `}
    ${({ type }) => type === 'info' && `
        color: #10285f;
        background: rgba(16, 40, 95, 0.08);
        border: 1px solid rgba(16, 40, 95, 0.18);
    `}

    svg { font-size: 1.3rem; flex-shrink: 0; }

    .MuiTypography-root {
        font-size: 0.86rem;
        font-weight: 700;
    }
`;
